// ============================================================
// hueco-cabecera.js — CUÁNTO TAPA la cabecera fija y cómo se le hace sitio a la 1ª sección.
//
// En «Ver sitio» y en el publicado la cabecera se saca de #resultado y va `position: fixed`
// (en el simulador móvil del editor, `absolute` sobre el marco): no ocupa sitio, así que la
// primera sección de la página empieza DEBAJO de ella. En el editor va en flujo y la sección
// empieza donde la cabecera acaba. Para que las dos se vean igual, la primera sección SUMA lo
// que tapa la cabecera a su propio padding-top (el del token de espaciado, el de su
// `data-spacing` o el que el bloque trae en línea): el texto queda a la misma distancia de la
// cabecera que en el editor, y el fondo de la sección (su foto, su color) sigue llegando hasta
// arriba, por debajo de la cabecera translúcida.
//
// Antes el hero no recibía nada, porque «ya reservaba su hueco» con un `padding-top: 180px`
// que en realidad nunca se aplicaba (lo pisa `#resultado section`, site.css); las demás
// secciones recibían `alto + 24 px`, o sea 24 px de aire frente a los 100-184 del editor.
//
// Lo usa `render.js::applyFirstSectionHeaderOffset`, que corre en las dos superficies servidas
// (render.js en preview/SPA, public-init.js en el publicado).
// ES2019 sin dependencias: lo prueba tests/_hueco_cabecera.mjs en node con un DOM de mentira.
// ============================================================

/**
 * Cuánto tapa la cabecera desde el borde superior de `raiz` (#resultado), en px enteros ≥ 0.
 * Es su BORDE INFERIOR, no su alto: la cabecera en píldora flota a `top: 20px` (site.css) y
 * tapa 20 px más de lo que mide. Se mide con el `top` computado y `offsetHeight`, que no ven el
 * `transform` de la animación de entrada (`headerReveal` la sube 10 px al nacer).
 *   · fixed    → `top` es del viewport, que con la página arriba coincide con el documento.
 *   · absolute → (simulador móvil) `top` es del documento: 80/100 px, los mismos que el marco.
 *   · en flujo → no debería llegar aquí; se mide su caja.
 * @param {HTMLElement} cabecera - la cabecera ya sacada de #resultado
 * @param {HTMLElement} [raiz] - #resultado (sin él, se mide contra el borde del documento)
 * @returns {number}
 */
export function reservaDeCabecera(cabecera, raiz) {
    const cs = getComputedStyle(cabecera);
    const posicionada = cs.position === 'fixed' || cs.position === 'absolute';
    const arriba = posicionada
        ? (parseFloat(cs.top) || 0)
        : cabecera.getBoundingClientRect().top + window.scrollY;
    const raizArriba = raiz ? raiz.getBoundingClientRect().top + window.scrollY : 0;
    return Math.max(0, Math.round(arriba + cabecera.offsetHeight - raizArriba));
}

// Padding PROPIO de cada sección (medido antes de tocarlo) y el valor que se le escribió encima.
const _propio = new WeakMap();

/**
 * Deja la sección con `padding-top` = su padding PROPIO + `reserva`, en línea y `!important`:
 * gana al `padding` en línea del bloque y a `data-spacing`, que también va con `!important`.
 * Idempotente: el propio se mide UNA vez, antes de escribir encima, y se reutiliza mientras el
 * valor en línea siga siendo el que se escribió aquí (las re-medidas por resize o por la carga
 * del logo solo cambian la reserva). Si otro código lo reescribió, se vuelve a medir.
 * @param {HTMLElement} seccion - la primera sección (o el <footer>) de la página
 * @param {number} reserva - lo que tapa la cabecera (px)
 */
export function reservarHueco(seccion, reserva) {
    const previo = _propio.get(seccion);
    const intacto = !!previo
        && seccion.style.getPropertyValue('padding-top') === previo.escrito
        && seccion.style.getPropertyPriority('padding-top') === 'important';
    const r = reserva > 0 ? reserva : 0;
    if (!intacto && !r) return;   // nada que reservar y nada escrito que actualizar
    const propio = intacto ? previo.propio : (parseFloat(getComputedStyle(seccion).paddingTop) || 0);
    const escrito = Math.round(propio + r) + 'px';
    seccion.style.setProperty('padding-top', escrito, 'important');
    _propio.set(seccion, { propio: propio, escrito: escrito });
}
